// src/features/urlaubsplaner/components/UrlaubFormModal.tsx

import React, { useState, useEffect } from 'react';
import { X, Loader } from 'lucide-react';
import { StaffUrlaubMitName, UrlaubFormData, AbwesenheitsArt, ABWESENHEITS_ARTEN } from '../types';
import { EmailSenderIdentity } from '../../../services/email/types';
import { staffService } from '../../../services/staff/StaffService';

interface UrlaubFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (formData: UrlaubFormData) => Promise<{ success: boolean; error?: string }>;
  onDelete?: (id: string) => Promise<{ success: boolean; error?: string }>;
  eintrag?: StaffUrlaubMitName | null;
  isAdmin: boolean;
  currentStaffId: string | null;
}

export const UrlaubFormModal: React.FC<UrlaubFormModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  eintrag,
  isAdmin,
  currentStaffId
}) => {
  const [staffListe, setStaffListe] = useState<EmailSenderIdentity[]>([]);
  const [staffId, setStaffId] = useState('');
  const [von, setVon] = useState('');
  const [bis, setBis] = useState('');
  const [art, setArt] = useState<AbwesenheitsArt>('urlaub');
  const [istHalberTag, setIstHalberTag] = useState(false);
  const [notiz, setNotiz] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Staff-Liste laden
  useEffect(() => {
    const loadStaff = async () => {
      const staff = await staffService.getActiveStaff();
      setStaffListe(staff);
    };
    if (isOpen) {
      loadStaff();
    }
  }, [isOpen]);

  // Formular mit Bearbeitungsdaten füllen
  useEffect(() => {
    if (eintrag) {
      setStaffId(eintrag.staff_id);
      setVon(eintrag.von);
      setBis(eintrag.bis);
      setArt(eintrag.art);
      setIstHalberTag(eintrag.ist_halber_tag || false);
      setNotiz(eintrag.notiz || '');
    } else {
      // Nicht-Admin: eigene ID vorauswählen
      setStaffId(!isAdmin && currentStaffId ? currentStaffId : '');
      setVon('');
      setBis('');
      setArt('urlaub');
      setIstHalberTag(false);
      setNotiz('');
    }
    setFormError(null);
    setShowDeleteConfirm(false);
  }, [eintrag, isOpen, isAdmin, currentStaffId]);

  // Halber Tag: bis automatisch auf von setzen
  const handleHalberTagChange = (checked: boolean) => {
    setIstHalberTag(checked);
    if (checked && von) {
      setBis(von);
    }
  };

  const handleSave = async () => {
    // Validierung
    if (!staffId) {
      setFormError('Bitte Mitarbeiter auswählen.');
      return;
    }
    if (!von) {
      setFormError('Bitte Startdatum eingeben.');
      return;
    }
    if (!bis) {
      setFormError('Bitte Enddatum eingeben.');
      return;
    }
    if (bis < von) {
      setFormError('Das Enddatum darf nicht vor dem Startdatum liegen.');
      return;
    }

    setSaving(true);
    setFormError(null);

    const formData: UrlaubFormData = {
      staff_id: staffId,
      von,
      bis: istHalberTag ? von : bis, // Bei halbem Tag: bis = von
      art,
      ist_halber_tag: istHalberTag,
      notiz
    };

    const result = await onSave(formData);

    if (result.success) {
      onClose();
    } else {
      setFormError(result.error || 'Fehler beim Speichern.');
    }

    setSaving(false);
  };

  const handleDelete = async () => {
    if (!eintrag || !onDelete) return;

    setDeleting(true);
    setFormError(null);

    const result = await onDelete(eintrag.id);

    if (result.success) {
      onClose();
    } else {
      setFormError(result.error || 'Fehler beim Löschen.');
    }

    setDeleting(false);
    setShowDeleteConfirm(false);
  };

  if (!isOpen) return null;

  // Für Nicht-Admins: Nur eigenen Namen im Dropdown anzeigen
  const verfuegbareStaff = isAdmin
    ? staffListe
    : staffListe.filter(staff => staff.id === currentStaffId);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        padding: '24px',
        width: '480px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: 0 }}>
            {eintrag ? 'Abwesenheit bearbeiten' : 'Neue Abwesenheit'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#6B7280',
              padding: '4px'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Fehlermeldung */}
        {formError && (
          <div style={{
            padding: '10px 14px',
            backgroundColor: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: '8px',
            color: '#DC2626',
            fontSize: '13px',
            marginBottom: '16px'
          }}>
            {formError}
          </div>
        )}

        {/* Mitarbeiter */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
            Mitarbeiter
          </label>
          <select
            value={staffId}
            onChange={(e) => setStaffId(e.target.value)}
            disabled={!isAdmin}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px',
              color: '#111827',
              backgroundColor: !isAdmin ? '#F9FAFB' : '#FFFFFF',
              boxSizing: 'border-box',
              cursor: !isAdmin ? 'not-allowed' : 'pointer'
            }}
          >
            <option value="">— Mitarbeiter auswählen —</option>
            {verfuegbareStaff.map(staff => (
              <option key={staff.id} value={staff.id}>
                {staff.fullName}
              </option>
            ))}
          </select>
        </div>

        {/* Art */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
            Art der Abwesenheit
          </label>
          <select
            value={art}
            onChange={(e) => setArt(e.target.value as AbwesenheitsArt)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px',
              color: '#111827',
              backgroundColor: '#FFFFFF',
              boxSizing: 'border-box'
            }}
          >
            {Object.entries(ABWESENHEITS_ARTEN).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {/* Halber Tag Checkbox */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            cursor: 'pointer',
            fontSize: '14px',
            color: '#374151'
          }}>
            <input
              type="checkbox"
              checked={istHalberTag}
              onChange={(e) => handleHalberTagChange(e.target.checked)}
              style={{
                width: '18px',
                height: '18px',
                accentColor: '#3B82F6',
                cursor: 'pointer'
              }}
            />
            <span style={{ fontWeight: '500' }}>Halber Tag</span>
            <span style={{ fontSize: '12px', color: '#9CA3AF' }}>(0,5 Tage)</span>
          </label>
        </div>

        {/* Datum Von / Bis */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
              {istHalberTag ? 'Datum' : 'Von'}
            </label>
            <input
              type="date"
              value={von}
              onChange={(e) => {
                setVon(e.target.value);
                if (istHalberTag) {
                  // Bei halbem Tag: bis immer gleich von
                  setBis(e.target.value);
                } else {
                  // Wenn bis leer oder kleiner als von, automatisch anpassen
                  if (!bis || bis < e.target.value) {
                    setBis(e.target.value);
                  }
                }
              }}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#111827',
                boxSizing: 'border-box'
              }}
            />
          </div>
          {/* Bis-Feld nur anzeigen wenn KEIN halber Tag */}
          {!istHalberTag && (
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Bis
              </label>
              <input
                type="date"
                value={bis}
                onChange={(e) => setBis(e.target.value)}
                min={von}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#111827',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          )}
        </div>

        {/* Notiz */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
            Notiz (optional)
          </label>
          <textarea
            value={notiz}
            onChange={(e) => setNotiz(e.target.value)}
            placeholder="z.B. Vertretung durch..."
            rows={3}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px',
              color: '#111827',
              resize: 'vertical',
              boxSizing: 'border-box',
              fontFamily: 'inherit'
            }}
          />
        </div>

        {/* Löschen-Bestätigung */}
        {showDeleteConfirm && (
          <div style={{
            padding: '12px 14px',
            backgroundColor: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: '8px',
            marginBottom: '16px'
          }}>
            <p style={{ fontSize: '13px', color: '#DC2626', margin: '0 0 10px 0' }}>
              Abwesenheit wirklich löschen?
            </p>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{
                  padding: '6px 14px',
                  backgroundColor: '#DC2626',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  opacity: deleting ? 0.7 : 1
                }}
              >
                {deleting ? 'Wird gelöscht...' : 'Ja, löschen'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  padding: '6px 14px',
                  backgroundColor: '#F3F4F6',
                  color: '#374151',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '13px',
                  cursor: 'pointer'
                }}
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}

        {/* Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: eintrag ? 'space-between' : 'flex-end',
          gap: '12px'
        }}>
          {/* Löschen-Button (nur bei Bearbeitung) */}
          {eintrag && onDelete && !showDeleteConfirm && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              style={{
                padding: '10px 18px',
                backgroundColor: '#FEF2F2',
                color: '#DC2626',
                border: '1px solid #FECACA',
                borderRadius: '8px',
                fontSize: '14px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              Löschen
            </button>
          )}

          <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
            <button
              onClick={onClose}
              style={{
                padding: '10px 18px',
                backgroundColor: '#F3F4F6',
                color: '#374151',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              Abbrechen
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '10px 18px',
                backgroundColor: '#3B82F6',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {saving && <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />}
              {saving ? 'Wird gespeichert...' : 'Speichern'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
